import { LocalRegion } from "@/models/share.model";
import { intlFormat } from "date-fns";
import enGB from 'date-fns/locale/en-GB'
import fr from 'date-fns/locale/fr'
import de from 'date-fns/locale/de'
import es from 'date-fns/locale/es'

const localesForDateFns = {
  'en': enGB,
  'fr': fr,
  'de': de,
  'es': es,
}

const localesForJs = {
  'en': 'en-GB',
  'fr': 'fr-FR',
  'de': 'de-DE',
  'es': 'es-ES',
}

export const getLocaleForDateFns = (localeString: string): Locale => {
  return localesForDateFns[localeString as keyof typeof localesForDateFns] || fr;
}

export const getLocaleForJs = (localeString: string): string => {
  return localesForJs[localeString as keyof typeof localesForJs] || 'fr-FR';
}


/**
 * format an offset time to the user timezone
 *
 * @param offsetTimeStr the offset time to convert
 * @return the time in the user timezone
 */
export const formatTime = (offsetTimeStr: string): string => {
    // create a date to convert to the user timezone
    const today = new Date();
    const isoDate = today.toISOString().split('T')[0];
    const isoDateTime = `${isoDate}T${offsetTimeStr}`;

    // convert to user timezone using new date
    const date = new Date(isoDateTime);

    // retrieve the converted time
    return date.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
    });
}

/**
 * format a time to the utc format
 *
 * @param timeStr the time to convert
 * @return the time in the utc format
 */
export const timeToUtcOffsetTime = (timeStr: string): string => {
    if (timeStr.endsWith('Z')){
        return timeStr;
    }
    const [hour, minute, second] = timeStr.split(':').map(Number);
    const localDate = new Date();
    localDate.setHours(hour, minute, second || 0, 0);
    const utcHour = localDate.getUTCHours().toString().padStart(2, '0');
    const utcMinute = localDate.getUTCMinutes().toString().padStart(2, '0');
    const utcSecond = localDate.getUTCSeconds().toString().padStart(2, '0');

    return `${utcHour}:${utcMinute}:${utcSecond}Z`;
}

/**
 * format a time to the utc format
 *
 * @param timeStr the time to convert
 * @return the time in the utc format
 */
export const timeToUtcOffsetTimeWithDate = (timeStr: string, date: Date): string => {
    if (timeStr.endsWith('Z')){
        return timeStr;
    }
    const [hour, minute, second] = timeStr.split(':').map(Number);

    date.setHours(hour, minute, second || 0, 0);
    const utcHour = date.getUTCHours().toString().padStart(2, '0');
    const utcMinute = date.getUTCMinutes().toString().padStart(2, '0');
    const utcSecond = date.getUTCSeconds().toString().padStart(2, '0');

    return `${utcHour}:${utcMinute}:${utcSecond}Z`;
}

/**
 * format an offset date time to the user timezone and convert it to time
 *
 * @param offsetDateTimeStr the offset date time to convert
 * @return the time in the user timezone
 */
export const formatUTCDateTimeToTime = (offsetDateTimeStr: string, localRegion?: LocalRegion): string => {
    // convert the offset date time to the user timezone
    return intlFormat(new Date(offsetDateTimeStr), {hour: '2-digit', minute: '2-digit', hour12: false}, {locale: localRegion ?? LocalRegion.frFR});
}

/**
 * format an offset date time to the user timezone and convert it to date
 *
 * @param offsetDateTimeStr the offset date time to convert
 * @return the date in the user timezone
 */
export const formatUTCDateTimeToDate = (offsetDateTimeStr: string, localRegion?: LocalRegion): string => {
    // convert the offset date time to the user timezone
    return intlFormat(new Date(offsetDateTimeStr), {}, {locale: localRegion ?? LocalRegion.frFR});
}

/**
 * Format a birthdate like a serial number (cns)
 * 
 * @param birthdate 
 * @returns the birthdate formatted
 */
export const getFormattedCnsPrexisFromBirthdate  = (birthdate: string): string => {
  if (birthdate) {
    return birthdate.replaceAll("/", " ");
  }
}

/**
 * Vérifie si une date correspond à hier ou à une date antérieure.
 */
export const isYesterdayOrBefore = (date: Date): boolean => {
  const today = new Date();
  const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());

  const compareDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  return compareDate.getTime() < startOfToday.getTime();
}
